/**
 * Analysexport av Affärer–Pipeline: hela leadslistan med kunder, kontakter,
 * all kommunikation, offerter, skedeshistorik och Flatbox-offerterna, som
 * flikar i en Excel-fil. Flikarna binds ihop med id-kolumner så att en
 * AI-assistent (eller en pivottabell) kan följa relationerna.
 *
 * Ren logik: tar emot data och returnerar flikar { namn, kolumner, rader }.
 * Inläsning sker i analysExportData.js, skrivning i utils/analysExcel.js.
 *
 * Inga bilder. Id-kolumnerna (*_id och kontakt_ids) står först i varje flik.
 * logo_url och foto_url är sökvägen till kopian i Firebase Storage.
 *
 * Personuppgifter (kontakternas e-post och telefon) ingår. Filen är för
 * internt bruk.
 */
import { matchaFlatboxOffertKund } from './kundmatchning';
import { toMillis, dagarISkede, skedeSedanMs, lokalDatum, OFFERT_STATUSAR } from './pipelineFlode';
import { sparadSokvag } from './bilder';
import { arIdKolumn } from './vanligExport';
import { urvalKundprojekt, kundprojektRader, kundSakerhetsfalt, utanHjalpfalt } from './kundprojektExport';

const MAX_CELL = 32000; // Excel tillåter 32 767 tecken per cell

const AKTIVITETSTYPER = {
  email: 'E-post', phone: 'Samtal', meeting: 'Möte', quote: 'Offert', note: 'Notering',
};
const RIKTNING = { in: 'Inkommande', out: 'Utgående' };

function datum(val) {
  const t = toMillis(val);
  return t == null ? '' : lokalDatum(t);
}

function datumTid(val) {
  const t = toMillis(val);
  if (t == null) return '';
  const d = new Date(t);
  return `${lokalDatum(t)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Kolumnnycklar ur första raden, id-kolumnerna först (i radens ordning). */
function nycklar(rader, forsta) {
  const alla = Object.keys(rader[0] || { [forsta]: '' });
  return [...alla.filter(arIdKolumn), ...alla.filter(x => !arIdKolumn(x))];
}

function text(v) {
  const s = v == null ? '' : String(v);
  return s.length > MAX_CELL ? `${s.slice(0, MAX_CELL)}\n[… avkortad, ${s.length} tecken]` : s;
}

const k = (key, header, width = 16) => ({ key, header, width });

/**
 * @param {object} d
 *   leads, companies, users, products, departments, stages,
 *   contactsByCompany {companyId: [contact]}, activitiesByLead {leadId: [activity]},
 *   offerterMap {leadId: [offert]}, flatboxOffers [doc] | null,
 *   kundprojekt [kundprojekt] (alla – urvalet görs här), allaLeads (för titlar)
 * @returns {{ blad: Array<{namn, kolumner, rader}>, statistik }}
 */
export function byggAnalysBlad(d) {
  const {
    leads, companies, users, products, departments, stages,
    contactsByCompany = {}, activitiesByLead = {}, offerterMap = {}, flatboxOffers = null,
    kundprojekt = [], allaLeads = leads,
  } = d;
  const now = Date.now();
  const kundMap = new Map(companies.map(c => [c.id, c]));
  const userNamn = id => {
    const u = users.find(x => x.id === id);
    return u ? (u.displayName || u.name || u.email || id) : (id || '');
  };
  const skede = key => stages.find(s => s.key === key)?.label || key || '';
  const produkt = id => products.find(p => p.id === id)?.name || '';
  const ao = ids => departments.filter(x => (ids || []).includes(x.id)).map(x => x.name).join(', ');

  const kundIds = [...new Set(leads.map(l => l.companyId).filter(Boolean))];

  // ── Flatbox-offerter, matchade mot CRM-kunder ──
  const flatbox = (flatboxOffers || []).map(o => {
    const off = o.offer || {};
    const m = matchaFlatboxOffertKund(off, companies);
    return { o, off, kund: m.kandidater[0] || null, via: m.via };
  });
  const flatboxPerKund = {};
  for (const f of flatbox) if (f.kund) (flatboxPerKund[f.kund.id] ||= []).push(f);

  // ── Leads ──
  const leadRader = leads.map(l => {
    const kund = kundMap.get(l.companyId);
    const huvud = (contactsByCompany[l.companyId] || []).find(c => c.id === l.mainContactId);
    const akt = activitiesByLead[l.id] || [];
    const senaste = [...akt].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))[0];
    return {
      lead_id: l.id,
      kund_id: l.companyId || '',
      kund: kund?.name || '',
      titel: l.title || '',
      skede: skede(l.status),
      skede_sedan: datum(skedeSedanMs(l)),
      dagar_i_skede: dagarISkede(l, now) ?? '',
      storlek: l.storlek || '',
      produkt: produkt(l.productId),
      typ: l.type || '',
      affarsomraden: ao(l.departmentIds),
      agare: userNamn(l.ownerId),
      skapad_av: userNamn(l.createdBy),
      skapad: datum(l.createdAt),
      uppdaterad: datum(l.updatedAt),
      kalla: l.kalla || '',
      nasta_steg: l.nastaSteg || '',
      nasta_uppfoljning: [l.nextFollowUp, l.nextFollowUpTime].filter(Boolean).join(' '),
      nasta_uppfoljning_status: l.nextFollowUp ? l.nextFollowUpStatus || '' : '',
      forvantat_avslut: l.expectedCloseDate || '',
      offertstatus: l.offerStatus || '',
      demostatus: l.demoStatus || '',
      sandbox: l.hasSandbox || l.status === 'sandbox' ? 'Ja' : '',
      samarbete: (l.tags || []).join(', '),
      huvudkontakt_id: l.mainContactId || '',
      huvudkontakt: huvud?.name || '',
      kontakt_ids: (l.contactIds || []).join(', '),
      antal_aktiviteter: akt.length,
      senaste_aktivitet_datum: senaste?.date || '',
      senaste_aktivitet: text(senaste?.summary || ''),
      antal_offerter: (offerterMap[l.id] || []).length,
      antal_flatbox_offerter_kund: (flatboxPerKund[l.companyId] || []).length,
      varde_ksek: l.estimatedValue ?? '',
      varde_enhet: l.revenueUnit || '',
      sannolikhet_procent: l.probability ?? '',
      prismodell: l.prismodell || '',
      driftsattning: l.deploymentType || '',
      plan: (Array.isArray(l.plan) ? l.plan : [l.plan].filter(Boolean)).join(', '),
      kommun: l.municipality || '',
      kontrakt_till: l.contractEndMonth || '',
      beskrivning: text(l.description),
    };
  });

  // ── Kunder ──
  const kundRader = kundIds.map(id => kundMap.get(id)).filter(Boolean).map(c => ({
    kund_id: c.id,
    namn: c.name || '',
    org_nr: c.orgNumber || '',
    comdeva_kundnummer: c.comdevaKundnummer || '',
    segment: c.segment || '',
    organisationstyp: c.organisationstyp || '',
    bransch: c.industry || '',
    ort: c.city || '',
    webbplats: c.website || '',
    telefon: c.phone || '',
    anstallda: c.employees || '',
    omsattning: c.revenue || '',
    kundstatus: c.status || '',
    affarsomraden: ao(c.departmentIds),
    linkedin: c.linkedin || c.linkedinUrl || '',
    logo_url: sparadSokvag(c, 'logo'),
    ...kundSakerhetsfalt(c),
    antal_leads: leads.filter(l => l.companyId === c.id).length,
    antal_flatbox_offerter: (flatboxPerKund[c.id] || []).length,
    beskrivning: text(c.description),
  }));

  // ── Kontakter ──
  const kontaktRader = [];
  for (const id of kundIds) {
    const kund = kundMap.get(id);
    for (const c of contactsByCompany[id] || []) {
      const iLeads = leads.filter(l => l.companyId === id && (l.contactIds || []).includes(c.id));
      kontaktRader.push({
        kontakt_id: c.id,
        kund_id: id,
        kund: kund?.name || '',
        namn: c.name || '',
        roll: c.role || '',
        avdelning: c.department || '',
        epost: c.email || '',
        mobil: c.mobile || '',
        telefon: c.phone || '',
        linkedin: c.linkedin || '',
        foto_url: sparadSokvag(c, 'foto'),
        saljroller: (Array.isArray(c.salesRoles) ? c.salesRoles : [c.dealRelation].filter(Boolean)).join(', '),
        relation: c.dealRelation || '',
        kopplad_till_leads: iLeads.map(l => l.id).join(', '),
        huvudkontakt_for_leads: iLeads.filter(l => l.mainContactId === c.id).map(l => l.id).join(', '),
        anteckningar: text(c.notes),
      });
    }
  }

  // ── Aktiviteter (all kommunikation) ──
  const aktivitetRader = [];
  for (const l of leads) {
    const akt = [...(activitiesByLead[l.id] || [])]
      .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
    for (const a of akt) {
      aktivitetRader.push({
        aktivitet_id: a.id,
        lead_id: l.id,
        kund_id: l.companyId || '',
        datum: a.date || datum(a.createdAt),
        typ: AKTIVITETSTYPER[a.type] || a.type || '',
        riktning: RIKTNING[a.direction] || '',
        kontakt_id: a.contactId || '',
        kontakt: a.contactName || '',
        deltagare: (a.participants || []).map(p => [p.name, p.email && `<${p.email}>`].filter(Boolean).join(' ')).join('; '),
        registrerad_av: userNamn(a.createdBy),
        registrerad: datumTid(a.createdAt),
        text: text(a.summary),
        mejltext: text(a.emailContent),
        uppfoljning: [a.followUpDate, a.followUpTime].filter(Boolean).join(' '),
        mote_start: a.start || '',
        mote_slut: a.slut || '',
        kallelse: a.kallelseStatus || '',
      });
    }
  }

  // ── Offerter registrerade på leads ──
  const offertRader = [];
  for (const l of leads) {
    for (const o of offerterMap[l.id] || []) {
      offertRader.push({
        offert_id: o.id,
        lead_id: l.id,
        kund_id: l.companyId || '',
        datum: o.datum || '',
        namn_niva: o.namn || '',
        belopp_ksek: o.beloppKSEK ?? '',
        status: OFFERT_STATUSAR.find(s => s.key === o.status)?.label || o.status || '',
        fil: o.fil?.name || '',
        registrerad_av: userNamn(o.skapadAv),
      });
    }
  }

  // ── Skedeshistorik ──
  const historikRader = [];
  for (const l of leads) {
    for (const h of l.skedeshistorik || []) {
      historikRader.push({
        lead_id: l.id,
        kund_id: l.companyId || '',
        fran: h.fran == null ? '(skapad)' : skede(h.fran),
        till: skede(h.till),
        tid: datumTid(h.tid),
        av: userNamn(h.av),
      });
    }
  }

  // ── Flatbox-offerter ──
  const flatboxRader = flatbox
    .sort((a, b) => String(a.off.offerNo || '').localeCompare(String(b.off.offerNo || ''), 'sv', { numeric: true }))
    .map(({ o, off, kund, via }) => {
      const c = o.config || {};
      const p = o.priceSnapshot || {};
      return {
        offertnr: off.offerNo || '',
        kund_id: kund?.id || '',
        matchad_via: via === 'orgnr' ? 'org.nr' : via === 'namn' ? 'namn' : via === 'doman' ? 'domän' : 'ingen träff',
        kund_har_lead_i_exporten: kund && leads.some(l => l.companyId === kund.id) ? 'Ja' : 'Nej',
        lead_ids_hos_kunden: kund ? leads.filter(l => l.companyId === kund.id).map(l => l.id).join(', ') : '',
        kund_i_offert: off.custName || '',
        org_nr: off.custOrg || '',
        kundadress: off.custAddr || '',
        kontakt: off.custContact || '',
        kontakt_epost: off.custEmail || '',
        kontakt_telefon: off.custPhone || '',
        saljare: off.seller || '',
        offertdatum: off.offerDate || '',
        giltig_till: off.offerValid || '',
        skickad: o.sent === true ? 'Ja' : o.sent === false ? 'Nej' : '',
        signeringsdatum: off.signeringsdatum || '',
        licens_start: off.licenseStart || '',
        licens_slut: off.licenseEnd || '',
        planerad_leverans: off.planeradLeveransdag || '',
        faktisk_leverans: off.faktiskLeveransdag || '',
        projekt: off.projName || '',
        projektnr: off.projNo || '',
        projektskede: off.projSkede || '',
        drift: c.deployment || '',
        omfattning: c.scope || '',
        anvandare: c.users ?? '',
        fakturering: c.billing || '',
        avtalsform: c.agreementForm || '',
        fas: c.phase || '',
        kampanj: c.campaignActive ? `Ja (${c.campaignMonths ?? ''} mån, ${Math.round((c.campaignDiscount || 0) * 100)} %)` : 'Nej',
        startavgift_betald: c.setupPaid ? 'Ja' : 'Nej',
        impenza: c.impenza ? 'Ja' : 'Nej',
        prisversion: p.pricingVersion ?? '',
        prisniva: p.tier || '',
        manadspris_kr: p.monthly ?? '',
        kampanj_manadspris_kr: p.campaignMonthly ?? '',
        forsta_perioden_kr: p.firstPeriod ?? '',
        forsta_fakturan_kr: p.firstInvoice ?? '',
        uppstartsavgift_kr: p.setupFee ?? '',
        sparad: datumTid(o.savedAt),
      };
    });

  const om = [
    { flik: 'Om filen', beskrivning: 'Den här fliken. Exporterad från LagbasN, Affärer – Pipeline.' },
    { flik: 'Leads', beskrivning: 'En rad per lead. lead_id är nyckeln; kund_id pekar på Kunder, huvudkontakt_id och kontakt_ids på Kontakter.' },
    { flik: 'Kunder', beskrivning: 'En rad per kund som har minst ett lead i exporten. kund_id är nyckeln.' },
    { flik: 'Kontakter', beskrivning: 'Alla kontakter hos kunderna. kopplad_till_leads listar de lead_id kontakten är kopplad till.' },
    { flik: 'Aktiviteter', beskrivning: 'All registrerad kommunikation: samtal, mejl (hela mejltexten i kolumnen mejltext när tråden lästs in), möten, offertutskick och anteckningar. En rad per händelse, sorterad på datum per lead.' },
    { flik: 'Lead-offerter', beskrivning: 'Offerter registrerade på leads i LagbasN (belopp i KSEK).' },
    { flik: 'Skedeshistorik', beskrivning: 'Varje skedesbyte. Loggas från 2026-10-19; äldre byten finns inte. "(skapad)" betyder att leadet skapades i skedet.' },
    { flik: 'Flatbox-offerter', beskrivning: flatboxOffers
      ? 'Alla offerter från Flatbox-konfiguratorn, matchade mot kunderna på org.nr, därefter namn, därefter e-postdomän. Priser i kronor enligt prislistan när offerten gjordes. Många offerter gäller fiktiva projekt.'
      : 'Kunde inte läsas (saknar behörighet till Flatbox, eller så är offerterna inte inlästa i LagbasN).' },
    { flik: '', beskrivning: '' },
    { flik: 'Skeden', beskrivning: stages.map(s => s.label).join(' → ') + '. Aktiva = Identifierad t.o.m. Pilot. Pilot = pilot eller kampanj pågår; räknas inte som vunnen och värdet inte som intäkt. Vilande = kunden har i princip tackat nej men sparas för framtiden.' },
    { flik: 'Storlek', beskrivning: 'S under 100 KSEK/år, M 100–500, L över 500.' },
    { flik: 'Kundprojekt', beskrivning: 'Projekt som kunderna driver eller deltar i, registrerade som säljunderlag (inte Tikabs egna uppdrag). Kundprojekt där minst en part är en kund i exporten. kundprojekt_id är nyckeln. testkorningar: testkörningar per kopplat lead.' },
    { flik: 'Kundprojektparter', beskrivning: 'En rad per part: kundprojekt_id → Kundprojekt, kund_id → Kunder, roll i projektet, lead_ids → Leads (kommaseparerade).' },
    { flik: 'Kundprojektkontakter', beskrivning: 'Kontakter kopplade till kundprojekt: kundprojekt_id, kontakt_id → Kontakter, roll i projektet.' },
    { flik: 'Säkerhetsflaggor', beskrivning: 'Kunder har erfarenhet_sakerhetsskydd, har_isolerad_miljo och sakerhets_anteckning; kundprojekt har säkerhetsfält. De anger bara ATT något gäller, aldrig var, vad eller hur. Endast för internt bruk.' },
    { flik: 'Personuppgifter', beskrivning: 'Filen innehåller namn, e-post och telefon till kundernas medarbetare. Endast för internt bruk inom Tikab.' },
  ];

  const kp = kundprojektRader({
    kundprojekt: urvalKundprojekt(kundprojekt, kundIds), companies, leads: allaLeads, contactsByCompany, users,
  });
  const kpRader = kp.kp;
  const kpParter = kp.parter.map(utanHjalpfalt);
  const kpKontakter = kp.kontakter.map(utanHjalpfalt);

  const blad = [
    { namn: 'Om filen', kolumner: [k('flik', 'Flik / begrepp', 22), k('beskrivning', 'Beskrivning', 110)], rader: om, wrap: ['beskrivning'] },
    { namn: 'Leads', kolumner: nycklar(leadRader, 'lead_id').map(x => k(x, x, ['titel', 'beskrivning', 'senaste_aktivitet'].includes(x) ? 40 : 16)), rader: leadRader, wrap: [] },
    { namn: 'Kunder', kolumner: nycklar(kundRader, 'kund_id').map(x => k(x, x, x === 'beskrivning' ? 50 : 18)), rader: kundRader, wrap: [] },
    { namn: 'Kontakter', kolumner: nycklar(kontaktRader, 'kontakt_id').map(x => k(x, x, 18)), rader: kontaktRader, wrap: [] },
    { namn: 'Aktiviteter', kolumner: nycklar(aktivitetRader, 'aktivitet_id').map(x => k(x, x, ['text', 'mejltext'].includes(x) ? 70 : 16)), rader: aktivitetRader, wrap: ['text', 'mejltext'] },
    { namn: 'Lead-offerter', kolumner: nycklar(offertRader, 'offert_id').map(x => k(x, x, 18)), rader: offertRader, wrap: [] },
    { namn: 'Skedeshistorik', kolumner: nycklar(historikRader, 'lead_id').map(x => k(x, x, 18)), rader: historikRader, wrap: [] },
    { namn: 'Flatbox-offerter', kolumner: nycklar(flatboxRader, 'offertnr').map(x => k(x, x, 16)), rader: flatboxRader, wrap: [] },
    { namn: 'Kundprojekt', kolumner: nycklar(kpRader, 'kundprojekt_id').map(x => k(x, x, ['namn', 'parter', 'beskrivning', 'testkorningar'].includes(x) ? 40 : 18)), rader: kpRader, wrap: [] },
    { namn: 'Kundprojektparter', kolumner: ['kundprojekt_id', 'kund_id', 'lead_ids', 'roll'].map(x => k(x, x, 22)), rader: kpParter, wrap: [] },
    { namn: 'Kundprojektkontakter', kolumner: ['kundprojekt_id', 'kontakt_id', 'roll_i_projektet'].map(x => k(x, x, 22)), rader: kpKontakter, wrap: [] },
  ];

  return {
    blad,
    statistik: {
      leads: leadRader.length, kunder: kundRader.length, kontakter: kontaktRader.length,
      aktiviteter: aktivitetRader.length, offerter: offertRader.length,
      flatbox: flatboxRader.length, flatboxMatchade: flatbox.filter(f => f.kund).length,
      kundprojekt: kpRader.length,
      flatboxLasbar: flatboxOffers != null,
    },
  };
}
